import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactClose,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from 'components/ai-elements/artifact';
import { DashboardPreview } from 'components/ai-elements/dashboard-preview';
import { SpecDashboard } from 'components/generative-ui';
import { JSXDashboard } from 'components/jsx-dashboard';
import { NotebookViewer } from 'components/notebook-viewer';
import { TableViewer } from 'components/table-viewer';
import { Button } from 'components/ui/button';
import { DownloadIcon, Maximize2Icon, Minimize2Icon, PresentationIcon } from 'lucide-react';
import React, { useCallback } from 'react';
import { ArtifactErrorBoundary } from 'sentry/artifact';
import { toast } from 'sonner';

import {
  getArtifactFilename,
  getArtifactTitle,
  getArtifactTypeInfo,
  isJsxFile,
  isNotebookFile,
  isPptxFile,
  isTabularFile,
} from '../utils/artifact-utils';

type ArtifactViewerProps = {
  url: string;
  mediaType: string | undefined;
  onClose: () => void;
  /** Streaming spec patches — when present, SpecDashboard renders from patches instead of URL. */
  patches?: object[];
  /** Base spec URL from a previous turn — merged with streaming patches for follow-up edits. */
  baseUrl?: string;
  /** Whether the pane is expanded to full width (chat panel collapsed). */
  isExpanded?: boolean;
  onToggleExpand?: () => void;
};

export const ArtifactViewer = ({
  url,
  mediaType,
  onClose,
  patches,
  baseUrl,
  isExpanded,
  onToggleExpand,
}: ArtifactViewerProps) => {
  const isStreamingSpec = mediaType === 'application/vnd.specstream+jsonl' && patches;
  const title = isStreamingSpec ? 'Dashboard' : getArtifactTitle(url);
  const typeInfo = getArtifactTypeInfo(mediaType, url);

  const handleDownload = useCallback(async () => {
    try {
      // The download attribute is ignored on cross-origin hrefs (the
      // presigned S3 URL), so fetch to a blob and download the object URL.
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = getArtifactFilename(url);
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Failed to download the artifact.');
    }
  }, [url]);

  const renderContent = () => {
    if (mediaType === 'application/vnd.specstream+jsonl') {
      return (
        <ArtifactErrorBoundary kind="specstream" url={isStreamingSpec ? baseUrl : url}>
          {isStreamingSpec
            ? <SpecDashboard patches={patches} baseUrl={baseUrl} />
            : <SpecDashboard url={url} />}
        </ArtifactErrorBoundary>
      );
    }
    if (mediaType === 'text/html') {
      return (
        <ArtifactErrorBoundary kind="html" url={url}>
          <DashboardPreview url={url} />
        </ArtifactErrorBoundary>
      );
    }
    if (isJsxFile(mediaType, url)) {
      return (
        <ArtifactErrorBoundary kind="jsx" url={url}>
          <JSXDashboard url={url} />
        </ArtifactErrorBoundary>
      );
    }
    if (isNotebookFile(mediaType, url)) {
      return (
        <ArtifactErrorBoundary kind="notebook" url={url}>
          <NotebookViewer url={url} />
        </ArtifactErrorBoundary>
      );
    }
    if (isTabularFile(mediaType, url)) {
      return (
        <ArtifactErrorBoundary kind="table" url={url}>
          <TableViewer url={url} mediaType={mediaType} />
        </ArtifactErrorBoundary>
      );
    }
    // PowerPoint decks can't render inline in the browser — offer a download.
    if (isPptxFile(mediaType, url)) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
          <PresentationIcon className="size-12 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              PowerPoint presentation — no inline preview. Download to open in PowerPoint or Keynote.
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={handleDownload}>
            <DownloadIcon className="mr-2 size-4" />
            Download
          </Button>
        </div>
      );
    }
    // Image fallback
    return (
      <div className="flex justify-center">
        <img
          src={url}
          alt={title}
          className="rounded-sm border border-border max-w-full h-auto"
          style={{ maxHeight: 600 }}
        />
      </div>
    );
  };

  return (
    <Artifact
      className="h-full border-l-0 border-t-0"
      // geometry contract for the marketing-video recorder — keep
      data-testid="artifact-panel"
    >
      <ArtifactHeader>
        <div className="min-w-0">
          <ArtifactTitle>{title}</ArtifactTitle>
          {typeInfo.label !== title && (
            <p className="text-xs text-muted-foreground">{typeInfo.label}</p>
          )}
        </div>
        <ArtifactActions>
          {/* SpecStream dashboards are not downloadable — the raw .spec.jsonl
              is an internal format (and streaming specs have no S3 object). */}
          {mediaType !== 'application/vnd.specstream+jsonl' && (
            <ArtifactAction
              icon={DownloadIcon}
              tooltip="Download"
              onClick={handleDownload}
            />
          )}
          {onToggleExpand && (
            <Button
              className="size-8 p-0 text-muted-foreground hover:text-foreground"
              size="sm"
              type="button"
              variant="ghost"
              onClick={onToggleExpand}
            >
              {isExpanded ? <Minimize2Icon className="size-4" /> : <Maximize2Icon className="size-4" />}
              <span className="sr-only">{isExpanded ? 'Collapse' : 'Expand'}</span>
            </Button>
          )}
          <ArtifactClose onClick={onClose} />
        </ArtifactActions>
      </ArtifactHeader>
      <ArtifactContent>
        {renderContent()}
      </ArtifactContent>
    </Artifact>
  );
};
